import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Stack,
  Divider,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import ConnectWithoutContactOutlinedIcon from '@mui/icons-material/ConnectWithoutContactOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import { useAuth } from '../firebase/authState';
import Navbar from './Navbar';

// Assets
import logo from '../assets/bouldou logo black.png';
import mainlogo from '../assets/JumbotronImageFinal.png';
import whiteTextLogo from '../assets/bouldou white.png';
import LandingFooter from '../components/LandingFooter';

// --- Theme Colors and Constants for Modern Look ---
const primaryColor = '#B200BB'; // Brighter, modern pink/red
const secondaryColor = '#FF3B30'; // Darker text/accent color
const lightGrey = '#F4F7FB';
const borderRadius = '12px';


// Replace this:
// <PricingSection navigate={navigate} />

// FIX: Define the Reusable Base Style Separately (Fixes the TypeError)
const AuthButtonBase = {
  fontSize: '0.9rem',
  fontWeight: 'bold',
  padding: '8px 18px',
  borderRadius: '25px',
  textTransform: 'none',
  boxShadow: 'none',
};

// --- New Component: Pricing Section ---
const PricingSection = ({ navigate }) => {
  const plans = [
    {
      title: 'Monthly Plan',
      price: 'Rs 500',
      frequency: '/month',
      isPopular: false,
      buttonText: 'Start Monthly',
      features: [
        'Full Profile Access',
        'Unlimited Swipes',
        'Direct Messaging',
        'Read Receipts',
      ],
      monthlyPriceDisplay: 'Rs 500',
    },
    {
      title: 'Yearly Plan',
      price: 'Rs 5000',
      frequency: '/year',
      isPopular: true,
      buttonText: 'Go Pro (Save Rs 1000!)',
      features: [
        'Full Profile Access',
        'Unlimited Swipes',
        'Direct Messaging',
        'Read Receipts',
      ],
      monthlyPriceDisplay: 'Rs 416.67',
    },
  ];

  const handleSubscribe = (plan) => {
    // Implement subscription logic or navigate to a dedicated checkout page
    console.log(`Subscribing to ${plan.title}`);
    navigate('/signup'); // Direct to signup as a call-to-action
  };

  return (
    <Box id="pricing" sx={{ ...styles.sectionContainer, backgroundColor: '#FFF' }}>
      <Container maxWidth="lg">
        <Typography variant="h3" sx={styles.sectionTitle}>
          Simple & Transparent Pricing
        </Typography>
        <Typography variant="body1" sx={{ ...styles.sectionText, mb: 6 }}>
          Unlock the full Bouldou experience with premium features designed to help you connect faster and better.
        </Typography>
        
        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={5} key={index}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <Card sx={plan.isPopular ? styles.pricingCardPopular : styles.pricingCard}>
                  <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} mb={2}>
                        <Typography variant="h5" sx={styles.pricingTitle}>
                            {plan.title}
                        </Typography>
                        {plan.isPopular && (
                            <Box sx={styles.popularBadge}>
                                <StarIcon sx={{ fontSize: '16px' }} />
                                Best Value
                            </Box>
                        )}
                    </Stack>

                    <Typography variant="h2" sx={styles.priceText}>
                      {plan.price}
                    </Typography>
                    <Typography variant="subtitle1" sx={styles.frequencyText}>
                      {plan.frequency}
                    </Typography>
                    <Typography variant="body2" sx={styles.monthlyEquivalentText}>
                        (Equivalent to {plan.monthlyPriceDisplay} / month)
                    </Typography>

                    <Divider sx={styles.pricingDivider} />

                    <Stack spacing={1.5} sx={{ my: 4 }}>
                      {plan.features.map((feature, idx) => (
                        <Box key={idx} sx={styles.featureItem}>
                          <CheckCircleOutlineIcon sx={styles.featureIcon} />
                          <Typography variant="body1" sx={styles.featureText}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>

                    <Button
                      variant={plan.isPopular ? 'contained' : 'outlined'}
                      size="large"
                      fullWidth
                      sx={plan.isPopular ? styles.pricingButtonContained : styles.pricingButtonOutlined}
                      onClick={() => handleSubscribe(plan)}
                    >
                      {plan.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};



const LandingPage = () => {
const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Function to scroll to a section by ID
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // ── Pricing Section Component ───────────────────────────────────────────────
const PricingSection = ({ navigate }) => {
  const plans = [
    {
      title: 'Monthly Plan',
      price: 'Rs 500',
      frequency: '/month',
      isPopular: false,
      buttonText: 'Choose Monthly',
      features: [
        'Unlimited swipes & matches',
        'Direct messaging',
        'Read receipts',
        'See who likes you',
        'Full profile visibility',
      ],
      monthlyEquivalent: null,
      savings: null,
    },
    {
      title: 'Yearly Plan',
      price: 'Rs 5,000',
      frequency: '/year',
      isPopular: true,
      buttonText: 'Best Value – Save 17%',
      features: [
        'Everything in Monthly',
        'Lowest price per month',
        'Full profile visibility',
        'Read receipts',
        'See who likes you',
      ],
      monthlyEquivalent: '≈ Rs 417 / month',
      savings: 'Save Rs 1,000',
    },
  ];

const handleChoosePlan = (plan) => {
  // Navigate to the payment page, passing the plan info in the state
  navigate('/payment-instructions', { state: { plan } });
};

  return (
    <Box id="pricing" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          align="center"
          sx={{
            fontFamily: customFontFamily,
            fontWeight: 800,
            color: secondaryColor,
            mb: 2,
          }}
        >
          Simple & Transparent Pricing
        </Typography>

        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 8, maxWidth: 720, mx: 'auto' }}
        >
          Unlock the full Bouldou experience — unlimited matches, messaging, and exclusive features.
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={5} key={plan.title}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card
                  elevation={plan.isPopular ? 12 : 4}
                  sx={{
                    height: '100%',
                    borderRadius: '20px',
                    border: plan.isPopular ? `3px solid ${primaryColor}` : `1px solid ${lightGrey}`,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.35s ease',
                    '&:hover': {
                      transform: 'translateY(-12px)',
                      boxShadow: plan.isPopular
                        ? '0 30px 60px rgba(178,0,187,0.25)'
                        : '0 20px 50px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  {plan.isPopular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        bgcolor: primaryColor,
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <StarIcon fontSize="small" />
                      Best Value
                    </Box>
                  )}

                  <CardContent sx={{ p: { xs: 4, md: 5 }, textAlign: 'center' }}>
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      color={secondaryColor}
                      gutterBottom
                    >
                      {plan.title}
                    </Typography>

                    <Typography
                      variant="h3"
                      fontWeight={900}
                      color={primaryColor}
                      sx={{ my: 2 }}
                    >
                      {plan.price}
                      <Typography component="span" variant="h6" color="text.secondary">
                        {plan.frequency}
                      </Typography>
                    </Typography>

                    {plan.monthlyEquivalent && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {plan.monthlyEquivalent}
                      </Typography>
                    )}

                    {plan.savings && (
                      <Typography
                        variant="subtitle2"
                        color="success.main"
                        fontWeight={600}
                        sx={{ mb: 3 }}
                      >
                        {plan.savings}
                      </Typography>
                    )}

                    <Divider sx={{ my: 3, bgcolor: lightGrey }} />

                    <Stack spacing={2} sx={{ mb: 5, textAlign: 'left' }}>
                      {plan.features.map((feature, i) => (
                        <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                          <CheckCircleOutlineIcon sx={{ color: primaryColor, fontSize: 22 }} />
                          <Typography variant="body1">{feature}</Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Button
                      variant={plan.isPopular ? 'contained' : 'outlined'}
                      size="large"
                      fullWidth
                      onClick={() => handleChoosePlan(plan)}
                      sx={{
                        py: 1.8,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        borderRadius: '50px',
                        ...(plan.isPopular
                          ? {
                              bgcolor: primaryColor,
                              '&:hover': { bgcolor: '#9A009A' },
                            }
                          : {
                              borderColor: primaryColor,
                              color: primaryColor,
                              '&:hover': {
                                bgcolor: `${primaryColor}10`,
                                borderColor: primaryColor,
                              },
                            }),
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

  // Modern Step Data with MUI Icons
  const steps = [
    {
      title: 'Create Your Profile',
      description: 'Sign up and build a vibrant profile that truly reflects your personality and interests.',
      icon: PersonAddAltOutlinedIcon,
    },
    {
      title: 'Discover Matches',
      description: 'Explore curated profiles and swipe to find people who share your passions and goals.',
      icon: ConnectWithoutContactOutlinedIcon,
    },
    {
      title: 'Connect & Chat',
      description: 'Start engaging conversations with your matches and form genuine, lasting connections.',
      icon: ChatBubbleOutlineOutlinedIcon,
    },
  ];

  // Contact Channels
  const contactChannels = [
    {
      label: 'Email',
      value: 'contactbouldou@gmail.com',
      icon: EmailOutlinedIcon,
      href: 'mailto:contactbouldou@gmail.com',
    },
    {
      label: 'WhatsApp',
      value: 'Chat with us',
      icon: WhatsAppIcon,
      href: 'https://whatsapp.com/channel/0029VbCR33xJENy15W0aRu13',
    },
    {
      label: 'Facebook',
      value: 'Bouldou Seychelles',
      icon: FacebookIcon,
      href: 'https://www.facebook.com/bouldou.seychelles?ref=1',
    },
  ];

  return (
    <Box sx={styles.mainContainer}>
      {/* Top Navigation Bar */}
     <Navbar />

      {/* ── Mobile Drawer Menu ─────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box sx={{ width: 280, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={700} color={primaryColor}>
              Menu
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <List>
            <ListItem button onClick={() => scrollToSection('objective')}>
              <ListItemText primary="Our Mission" />
            </ListItem>
            <ListItem button onClick={() => scrollToSection('how-to-start')}>
              <ListItemText primary="How to Start" />
            </ListItem>
            <ListItem button onClick={() => scrollToSection('pricing')}>
              <ListItemText primary="Pricing" />
            </ListItem>
            <ListItem button onClick={() => scrollToSection('contact')}>
              <ListItemText primary="Contact Us" />
            </ListItem>

            <Divider sx={{ my: 2 }} />

            {user ? (
              <ListItem button onClick={logout}>
                <ListItemText primary="Log Out" />
              </ListItem>
            ) : (
              <>
                <ListItem button onClick={() => { navigate('/signin'); setMobileMenuOpen(false); }}>
                  <ListItemText primary="Log In" />
                </ListItem>
                <ListItem button onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }}>
                  <ListItemText primary="Sign Up" />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>

      {/* Jumbotron Section - Modern Gradient and Layout */}
      <Box sx={styles.jumbotronContainer}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h5" sx={styles.jumbotronTitle}>
                Zwenn li lo <span><img style={{marginBottom: '-10px'}} src={whiteTextLogo} alt='Bouldou White text logo'/></span>
              </Typography>
              <Typography variant="h6" sx={styles.jumbotronSubtitle}>
                Join a vibrant community to meet new people and <br/> build meaningful, lasting connections today.
              </Typography>
              
{/* <Button
  variant="contained"
  size="large"
  sx={{
    backgroundColor: '#FFD700',
    color: '#000',
    fontWeight: 'bold',
    px: 6,
    py: 1.5,
    mt: 3,
    borderRadius: '50px',
    '&:hover': { backgroundColor: '#FFC107' }
  }}
  onClick={() => navigate('/subscription')}
>
  Upgrade to Premium – Start Now
</Button> */}
              {/* ==== JUMBOTRON BUTTONS – conditional ==== */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 4 }}>
                {!authLoading && user ? (
                  <Button
                    variant="contained"
                    size="large"
                    sx={styles.signUpButton}
                    onClick={() => navigate('/home')}   // or wherever your dashboard lives
                  >
                    Go to Account
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      sx={styles.signUpButton}
                      onClick={() => navigate('/signup')}
                    >
                      Get Started Now
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      sx={styles.loginButton}
                      onClick={() => navigate('/signin')}
                    >
                      Log In
                    </Button>
                  </>
                )}
              </Stack>
            </Grid>

            {/* Image stays the same */}
            <Grid item xs={12} md={6} sx={{ position: 'relative', height: { xs: '200px', md: '400px' } }}>
              <Box component="img" src={mainlogo} alt="Bouldou App Logo" sx={styles.jumbotronImage} />
            </Grid>
          </Grid>
        </Container>
        <Box sx={styles.jumbotronWaveDivider} />
      </Box>


      {/* Objective Section - Clean and Focused */}
      <Box id="objective" sx={styles.sectionContainer}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={styles.sectionTitle}>
            Our Mission
          </Typography>
          <Typography variant="body1" sx={styles.sectionText}>
            Our mission is to create a safe and engaging platform where individuals can connect, share interests, and form lasting relationships. We aim to foster a community that celebrates diversity, encourages authentic interactions, and empowers users to find meaningful connections without compromise.
          </Typography>
        </Container>
      </Box>

      {/* How to Start Section - Card Focus and Modern Icons */}
      <Box id="how-to-start" sx={{ ...styles.sectionContainer, backgroundColor: lightGrey }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={styles.sectionTitle}>
            Ready to Connect? Three Simple Steps
          </Typography>
          <Grid container spacing={4} sx={{ mt: 3 }}>
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{m:'auto'}}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                >
                  <Card sx={styles.stepCard}>
                    <CardContent sx={styles.stepCardContent}>
                      <Box sx={styles.stepIconContainer}>
                        <step.icon sx={styles.stepIcon} />
                      </Box>
                      <Typography variant="h6" sx={styles.stepTitle}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" sx={styles.stepDescription}>
                        {step.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
          
        </Container>

      </Box>
        {/* <PricingSection navigate={navigate} /> */}

      {/* Contact Us Section */}
      <Box id="contact" sx={{ ...styles.sectionContainer }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={styles.sectionTitle}>
            Contact Us
          </Typography>
          <Typography variant="body1" sx={{ ...styles.sectionText, mb: 6 }}>
            Have a question, feedback, or need support? We'd love to hear from you — reach out through any of the channels below.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {contactChannels.map((channel, index) => (
              <Grid item xs={12} sm={4} key={channel.label}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                >
                  <Card
                    component="a"
                    href={channel.href}
                    target={channel.href.startsWith('http') ? '_blank' : undefined}
                    rel={channel.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    sx={styles.contactCard}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <Box sx={styles.contactCardIconContainer}>
                        <channel.icon sx={styles.contactCardIcon} />
                      </Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: secondaryColor }}>
                        {channel.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {channel.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <LandingFooter />
    </Box>
  );
};

// --- Define the new custom font family ---
const customFontFamily = 'Poppins, Arial, sans-serif';

const styles = {
  // Existing Styles (omitted for brevity, but they are all still here)
  mainContainer: {
    backgroundColor: '#FFF',
    pb: '60px',
    pt: '64px', // Offset for fixed AppBar
  },
  // Top Navigation Bar Styles
  appBar: {
    backgroundColor: '#FFF',
    borderBottom: `1px solid ${lightGrey}`,
  },
  toolbar: {
    justifyContent: 'space-between',
    alignItems: 'center',
    py: 1,
  },
  logoImage: {
    height: { xs: '30px', md: '40px' },
    width: 'auto',
    mr: { xs: 1, md: 5 },
  },
  navLinks: {
    flexGrow: 1,
    justifyContent: 'flex-start',
    display: { xs: 'none', md: 'flex' }, // Hide links on small screens for cleaner look
  },
  navButton: {
    color: 'gray',
    fontSize: '0.95rem',
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': {
      color: primaryColor,
      backgroundColor: 'transparent',
    },
  },
  navSignUpButton: {
    ...AuthButtonBase,
    backgroundColor: primaryColor,
    color: '#FFF',
    '&:hover': {
      backgroundColor: '#E0476E',
      boxShadow: '0 4px 15px rgba(255, 82, 123, 0.4)',
    },
    display: { xs: 'none', md: 'flex' },
  },
  navLoginButton: {
    ...AuthButtonBase,
    borderColor: primaryColor,
    color: primaryColor,
    '&:hover': {
      borderColor: '#E0476E',
      color: '#E0476E',
      backgroundColor: `${primaryColor}1A`,
    },
    display: { xs: 'none', sm: 'flex' },
  },
  // Jumbotron Styles - Modern Gradient and Typography
  jumbotronContainer: {
    position: 'relative', // Key for positioning the divider
    maxHeight: { xs: '75vh', sm: '40vh', md: '40vh', lg: '38vh' },
    justifyContent: 'center',
    pt: { xs: '80px', sm: '100px', md: '120px' },
    pb: { xs: '80px', sm: '60px', md: '100px' }, // Increased padding to make room for the wave
    background: 'linear-gradient(135deg, #B200BB 0%, #CF003D 100%)',
    overflow: 'hidden',
  },
jumbotronTitle: {
    fontFamily: customFontFamily,
    fontSize: { xs: '2rem', sm: '3rem', md: '3rem' },
    fontWeight: 800,
    color: '#FFF',
    lineHeight: 1.1,
    mb: 2,
    textAlign: { xs: 'center' },
    marginLeft: { xs: 0, md: -10 }
  },
  jumbotronSubtitle: {
    fontFamily: customFontFamily,
    fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' },
    color: 'rgba(255, 255, 255, 0.9)',
    mb: '20px',
    mx: {xs: 3, md: 0},
    lineHeight: 1.5,
    textAlign: { xs: 'center', md: 'left' },
  },
  // Modified jumbotronImageWrapper for better centering and responsiveness
  jumbotronImageWrapper: {
    display: 'flex',
    justifyContent: 'center', // Center horizontally
    alignItems: 'center',     // Center vertically
    height: '100%',           // Take full height to center the image within the grid item
    pt: { xs: 4, md: 0 },     // Add some top padding on small screens if text is above
  },
  jumbotronImage: {
    position: 'relative',
    top: { xs: '55%', md: '-9%' }, // Center vertically
    right: { xs: '50%', md: '-500px' }, // Position on the right, adjust for responsiveness
    transform: { xs: 'translate(50%, -50%)', md: 'translate(0, -50%)' }, // Center on mobile, align right on desktop
    width: { xs: '100%', sm: '100%', md: '100%' }, // Responsive width
    maxWidth: '850px', // Max width to prevent it from getting too large
    height: 'auto',
    filter: 'drop-shadow(0px 8px 15px rgba(0,0,0,0.2))', // Modern subtle shadow
  },
  signUpButton: {
    ...AuthButtonBase,
    backgroundColor: '#FFF',
    color: primaryColor,
    padding: '12px 35px',
    '&:hover': {
      backgroundColor: lightGrey,
      boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
    },
  },
  loginButton: {
    ...AuthButtonBase,
    borderColor: '#FFF',
    color: '#FFF',
    zIndex: 100,
    padding: '12px 35px',
    '&:hover': {
      borderColor: lightGrey,
      color: lightGrey,
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
  },

  // Section Styles
  sectionContainer: {
    py: { xs: '60px', md: '100px' },
  },
  sectionTitle: {
    fontFamily: customFontFamily, // Applied new font
    fontSize: { xs: '2rem', md: '3rem' },
    fontWeight: 700,
    color: 'black',
    textAlign: 'center',
    mb: '40px',
  },
  sectionText: {
    fontSize: '1.15rem',
    color: '#666',
    textAlign: 'center',
    maxWidth: '900px',
    mx: 'auto',
    lineHeight: 1.7,
  },
  // How to Start Styles
  stepCard: {
    borderRadius: borderRadius,
    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
    textAlign: 'center',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 12px 35px rgba(0,0,0,0.15)',
    },
    height: '100%',
    maxWidth: '300px',
    marginLeft: {xs:'28px', md:''}
  },
  stepCardContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    p: '30px',
  },
  stepIconContainer: {
    backgroundColor: `${primaryColor}20`,
    borderRadius: '50%',
    p: 2,
    mb: '20px',
  },
  stepIcon: {
    fontSize: '35px',
    color: primaryColor,
  },
  stepTitle: {
    fontFamily: customFontFamily, // Applied new font
    fontSize: '1.4rem',
    fontWeight: 700,
    color: secondaryColor,
    mb: '10px',
  },
  stepDescription: {
    fontSize: '1rem',
    color: '#777',
    lineHeight: 1.6,
  },
  // Contact Us Styles
  contactInfo: {
    mt: '30px',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 0',
  },
  contactIcon: {
    color: primaryColor,
    fontSize: '28px',
  },
  contactText: {
    fontSize: '1.05rem',
    color: secondaryColor,
    fontWeight: 500,
  },
  contactCard: {
    display: 'block',
    borderRadius: borderRadius,
    boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
    textDecoration: 'none',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 12px 35px rgba(0,0,0,0.15)',
    },
  },
  contactCardIconContainer: {
    backgroundColor: `${primaryColor}20`,
    borderRadius: '50%',
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mx: 'auto',
    mb: '16px',
  },
  contactCardIcon: {
    fontSize: '30px',
    color: primaryColor,
  },
  contactFormCard: {
    borderRadius: '15px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
  },
  formTitle: {
    fontFamily: customFontFamily, // Applied new font
    fontSize: '1.5rem',
    fontWeight: 700,
    color: secondaryColor,
    mb: '25px',
    textAlign: 'center',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      transition: 'border-color 0.3s',
      '& fieldset': {
        borderColor: lightGrey,
      },
      '&:hover fieldset': {
        borderColor: primaryColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: primaryColor,
        borderWidth: '2px',
      },
    },
  },
  submitButton: {
    backgroundColor: primaryColor,
    color: '#FFF',
    fontSize: '1rem',
    fontWeight: 'bold',
    padding: '12px',
    borderRadius: '8px',
    mt: '20px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#E0476E',
      boxShadow: '0 4px 15px rgba(255, 82, 123, 0.4)',
    },
  },
  // Added a style for section subtitles, if you choose to use Typography variant h5 for them
  sectionSubtitle: {
    fontFamily: customFontFamily,
    fontWeight: 600,
    fontSize: '1.75rem', // Example size, adjust as needed
  },

  // --- NEW: Pricing Section Styles ---
  pricingCard: {
    borderRadius: '20px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    textAlign: 'center',
    height: '100%',
    border: `2px solid ${lightGrey}`,
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 15px 40px rgba(0, 0, 0, 0.15)',
    },
  },
  pricingCardPopular: {
    borderRadius: '20px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
    textAlign: 'center',
    height: '100%',
    border: `3px solid ${primaryColor}`,
    position: 'relative',
    transform: 'scale(1.03)',
    '&:hover': {
        transform: 'translateY(-5px) scale(1.03)',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)',
    },
  },
  popularBadge: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: secondaryColor,
    color: '#FFF',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    px: 2,
    py: 0.5,
  },
  pricingTitle: {
    fontFamily: customFontFamily,
    fontWeight: 700,
    color: secondaryColor,
    fontSize: '1.6rem',
  },
  priceText: {
    fontFamily: customFontFamily,
    fontWeight: 800,
    fontSize: '3rem',
    color: primaryColor,
    lineHeight: 1,
    mt: 2,
  },
  frequencyText: {
    color: '#666',
    fontWeight: 600,
    fontSize: '1.2rem',
    mt: 1,
  },
  monthlyEquivalentText: {
    color: '#999',
    fontSize: '0.9rem',
    fontStyle: 'italic',
    mt: 0.5,
  },
  pricingDivider: {
    my: 3,
    backgroundColor: lightGrey,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    textAlign: 'left',
  },
  featureIcon: {
    color: primaryColor,
    fontSize: '20px',
  },
  featureText: {
    color: '#444',
    fontSize: '1.05rem',
  },
  pricingButtonOutlined: {
    ...AuthButtonBase,
    fontSize: '1rem',
    padding: '12px 0',
    borderColor: primaryColor,
    color: primaryColor,
    mt: 3,
    '&:hover': {
      backgroundColor: `${primaryColor}1A`,
      borderColor: primaryColor,
    },
  },
  pricingButtonContained: {
    ...AuthButtonBase,
    fontSize: '1rem',
    padding: '12px 0',
    backgroundColor: primaryColor,
    color: '#FFF',
    mt: 3,
    '&:hover': {
      backgroundColor: '#E0476E',
      boxShadow: '0 4px 15px rgba(255, 82, 123, 0.4)',
    },
  }
};

export default LandingPage;